import { useEffect, useRef, useState } from "react";
import { createCommandSender } from "./academyApi";
import { checkIdentitySession } from "./identityClient";
import { useIdentity } from "./useIdentity";
import {
  ACADEMIC_CATALOGUE_CHANGED,
  readAcademicProgramVersions,
  useAcademicRead,
} from "./useAcademicCatalogue";
import type {
  AcademicProgramReceipt,
  ReviseAcademicProgram,
  CreateAcademicProgram,
} from "./generated/academic-catalogue";

/** Approved code/reason controls within the original Program Portfolio dialog. */
export function AcademicProgramFields(props: {
  action?: "submit" | "approve" | "request-changes" | "publish";
  record?: Record<string, unknown>;
  formId: string;
  onSaved: () => void;
  onBusy: (busy: boolean) => void;
}) {
  const account = useIdentity();
  const membership = account.access?.memberships.find(
    item => item.workspaceId === account.session?.workspace.id,
  );
  if (!account.session || !membership?.institutionId)
    return <p role="alert">Load your current Institution membership before creating a Program.</p>;
  const context = `${account.session.tenantId}:${account.session.user.id}:${account.session.workspace.id}`;
  return (
    <ProgramFields
      key={`${context}:${props.record?.id || "new"}:${props.action || "edit"}`}
      {...props}
      context={context}
      institutionId={membership.institutionId}
      ownerName={account.session.workspace.name}
      canManage={account.session.permissions.includes(
        props.action === "publish"
          ? "institution.curriculum.publish"
          : props.action === "approve" || props.action === "request-changes"
            ? "institution.curriculum.approve"
            : "institution.curriculum.manage",
      )}
    />
  );
}

function ProgramFields({
  action,
  record,
  formId,
  onSaved,
  onBusy,
  context,
  institutionId,
  ownerName,
  canManage,
}: Parameters<typeof AcademicProgramFields>[0] & {
  context: string;
  institutionId: string;
  ownerName: string;
  canManage: boolean;
}) {
  const [name, setName] = useState(""),
    [code, setCode] = useState(""),
    [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const sender = useRef(createCommandSender({ headerOnly: true }));
  const submitting = useRef(false);
  const path = `academic-catalogue/programs/${encodeURIComponent(String(record?.id || ""))}/version-history`;
  const versions = useAcademicRead(path, !!record, signal =>
    readAcademicProgramVersions(String(record!.id), signal),
  );
  const expectedState =
    action === "publish"
      ? "approved"
      : action === "approve" || action === "request-changes"
        ? "in_review"
        : "draft";
  const latest = versions.data?.items.slice().sort((left, right) => right.versionNo - left.versionNo)[0];
  const source =
    versions.data?.items.find(item => item.status === expectedState) ||
    (!action
      ? latest?.status === "rejected"
        ? latest
        : versions.data?.items.find(item => item.id === versions.data?.currentVersionId)
      : undefined);
  const pending = versions.data?.items.some(item =>
    ["in_review", "approved", "scheduled"].includes(item.status),
  );
  const initialized = useRef(false);
  useEffect(() => {
    if (source && !initialized.current) {
      setName(source.name);
      setCode(source.code);
      initialized.current = true;
    }
  }, [source]);
  const ready =
    canManage && (!record || (!!source && (!!action || !pending) && !versions.loading && !versions.error));
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (submitting.current || !ready) return;
    submitting.current = true;
    setBusy(true);
    onBusy(true);
    setError("");
    try {
      const session = await checkIdentitySession();
      if (`${session.tenantId}:${session.user.id}:${session.workspace.id}` !== context)
        throw new Error("Your workspace changed. Reload before creating this Program.");
      if (record && source && action) {
        await sender.current(
          `academic-catalogue/programs/${record.id}/versions/${source.id}/commands/${action}`,
          { expectedVersion: source.version, reason: reason.trim() },
        );
      } else if (record && source && versions.data) {
        const input: ReviseAcademicProgram = {
          expectedVersion: versions.data.version,
          sourceVersionId: source.id,
          name: name.trim(),
          reason: reason.trim(),
        };
        await sender.current(
          `academic-catalogue/programs/${record.id}/commands/${source.status === "draft" ? "update" : "revise"}`,
          input,
        );
      } else {
        const input: CreateAcademicProgram = {
          institutionId,
          code: code.trim(),
          name: name.trim(),
          reason: reason.trim(),
        };
        await sender.current<AcademicProgramReceipt>("academic-catalogue/programs/commands/create", input);
      }
      window.dispatchEvent(new Event(ACADEMIC_CATALOGUE_CHANGED));
      onSaved();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "The Program could not be created.");
    } finally {
      submitting.current = false;
      setBusy(false);
      onBusy(false);
    }
  };
  return (
    <form id={formId} onSubmit={submit} className="form-fields-v2">
      {versions.loading && (
        <p role="status" className="full">
          Loading the exact Program version…
        </p>
      )}
      {versions.error && (
        <p role="alert" className="full">
          {versions.error}
        </p>
      )}
      {pending && !action && (
        <p role="status" className="full">
          This Program has a version awaiting review or publication. Resolve it before making another
          revision.
        </p>
      )}
      {error && (
        <p role="alert" className="full">
          {error}
        </p>
      )}
      {!canManage && (
        <p role="alert" className="full">
          Current Program management permission is required.
        </p>
      )}
      <div className="form-field-v2 full">
        <label htmlFor={`${formId}-name`}>Name / title</label>
        <input
          id={`${formId}-name`}
          autoFocus
          required
          minLength={2}
          maxLength={200}
          readOnly={!!action}
          value={name}
          onChange={event => setName(event.target.value)}
          disabled={busy}
        />
      </div>
      <div className="form-field-v2">
        <label htmlFor={`${formId}-owner`}>Owner</label>
        <input id={`${formId}-owner`} readOnly value={ownerName} />
      </div>
      <div className="form-field-v2">
        <label htmlFor={`${formId}-scope`}>Scope</label>
        <input id={`${formId}-scope`} readOnly value="Current Institution" />
      </div>
      <div className="form-field-v2">
        <label htmlFor={`${formId}-state`}>{record ? "Current version" : "Initial status"}</label>
        <input
          id={`${formId}-state`}
          readOnly
          value={source ? `${source.status} · Version ${source.versionNo}` : "Draft"}
        />
      </div>
      <div className="form-field-v2">
        <label htmlFor={`${formId}-code`}>Program code</label>
        <input
          id={`${formId}-code`}
          required
          minLength={2}
          maxLength={120}
          pattern="[a-z0-9]+(-[a-z0-9]+)*"
          value={code}
          onChange={event => setCode(event.target.value)}
          readOnly={!!record}
          disabled={busy}
        />
      </div>
      <div className="form-field-v2 full">
        <label htmlFor={`${formId}-reason`}>Reason for change</label>
        <textarea
          id={`${formId}-reason`}
          required
          minLength={10}
          maxLength={2000}
          value={reason}
          onChange={event => setReason(event.target.value)}
          disabled={busy}
        />
      </div>
    </form>
  );
}
